/**
 * Decision Engine - Loss Causes Management
 *
 * Manages the loss cause taxonomy for AI-powered claim classification.
 * Flat structure with primary/secondary loss cause fields (v2 SafetyCulture format).
 */

import { PageController } from '@client/components/_core/layout/PageController';
import { TableToolbar } from '@client/components/_core/table/TableToolbar';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { errorMessage } from '@client/components/_ui/feedback/error-message/ErrorMessage';
import { message } from '@client/components/_ui/feedback/message';
import { Space } from '@client/components/_ui/layout/Space';
import { useTrpc } from '@client/hooks/api/useTrpc';
import { createColumnHelper, PrismaTable, usePrismaTable } from '@client/hooks/table-v2';
import { createFileRoute, useParams } from '@tanstack/react-router';
import { Badge, Drawer, Form, Input, InputNumber, Switch } from 'antd';
import { GitBranch, Plus } from 'lucide-react';
import { useMemo, useState } from 'react';

type LossCauseFormInput = {
    primaryLossCause: string;
    secondaryLossCause: string;
    lossCauseKey: string;
    plainDescription: string | null;
    classificationRule: string | null;
    aiKeywords: string | null;
    exampleClaimDescription: string | null;
    sortOrder: number;
    isActive: boolean;
};

const FormItem = Form.Item<LossCauseFormInput>;

export const Route = createFileRoute(
    '/console/$orgId/configuration/claims-products/product-types/$productTypeId/decision-engine/loss-causes',
)({
    staticData: {
        metadata: {
            title: 'Loss Causes',
        },
    },
    component: LossCausesManagement,
});

function LossCausesManagement() {
    const { productTypeId } = useParams({ strict: false });
    const { trpc, trpcUtils } = useTrpc();
    const [form] = Form.useForm<LossCauseFormInput>();

    // Drawer state
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [editingId, setEditingId] = useState<bigint | null>(null);
    const formId = 'loss-cause-form';

    // Fetch all loss causes
    const { data, isLoading } = trpc.claim.decisionEngine.admin.listLossCauses.useQuery(
        { productTypeId: BigInt(productTypeId!) },
        { enabled: !!productTypeId },
    );
    const lossCauses = data?.rows ?? [];

    // Mutations
    const { mutate: createLossCause, isPending: isCreating } =
        trpc.claim.decisionEngine.admin.createLossCause.useMutation({
            onSuccess() {
                message.success('Loss cause created.');
                void trpcUtils.claim.decisionEngine.admin.listLossCauses.invalidate();
                void trpcUtils.claim.decisionEngine.getConfig.invalidate({ productTypeId: BigInt(productTypeId!) });
                closeDrawer();
            },
            onError(error) {
                errorMessage.show(error);
            },
        });

    const { mutate: updateLossCause, isPending: isUpdating } =
        trpc.claim.decisionEngine.admin.updateLossCause.useMutation({
            onSuccess() {
                message.success('Loss cause updated.');
                void trpcUtils.claim.decisionEngine.admin.listLossCauses.invalidate();
                void trpcUtils.claim.decisionEngine.getConfig.invalidate({ productTypeId: BigInt(productTypeId!) });
                closeDrawer();
            },
            onError(error) {
                errorMessage.show(error);
            },
        });

    const { mutate: deleteLossCause } = trpc.claim.decisionEngine.admin.deleteLossCause.useMutation({
        onSuccess() {
            message.success('Loss cause deleted.');
            void trpcUtils.claim.decisionEngine.admin.listLossCauses.invalidate();
            void trpcUtils.claim.decisionEngine.getConfig.invalidate({ productTypeId: BigInt(productTypeId!) });
        },
        onError(error) {
            errorMessage.show(error);
        },
    });

    // Table columns
    const columnHelper = createColumnHelper<(typeof lossCauses)[number]>();

    const columns = useMemo(
        () => [
            columnHelper.accessor('primaryLossCause', {
                id: 'primaryLossCause',
                header: 'Primary loss cause',
                width: 200,
            }),
            columnHelper.accessor('secondaryLossCause', {
                id: 'secondaryLossCause',
                header: 'Secondary loss cause',
                width: 200,
            }),
            columnHelper.accessor('lossCauseKey', {
                id: 'lossCauseKey',
                header: 'Key',
                width: 180,
                cell: ({ row }) => <code className="rounded bg-muted px-1.5 py-0.5 text-xs">{row.lossCauseKey}</code>,
            }),
            columnHelper.accessor('plainDescription', {
                id: 'plainDescription',
                header: 'Description',
                width: 250,
                cell: ({ row }) => {
                    if (!row.plainDescription) return <span className="text-muted-foreground">—</span>;
                    const truncated =
                        row.plainDescription.length > 80
                            ? `${row.plainDescription.slice(0, 80)}...`
                            : row.plainDescription;
                    return <span title={row.plainDescription}>{truncated}</span>;
                },
            }),
            columnHelper.accessor('isActive', {
                id: 'isActive',
                header: 'Status',
                width: 100,
                cell: ({ row }) => (
                    <Badge status={row.isActive ? 'success' : 'default'} text={row.isActive ? 'Active' : 'Inactive'} />
                ),
            }),
            columnHelper.actions({
                id: 'actions',
                cell: ({ row }) => (
                    <Space size="small">
                        <Button.Edit onClick={() => openEditDrawer(row)} />
                        <Button.Delete
                            confirmTitle="Delete loss cause"
                            confirmDescription="Are you sure you want to delete this loss cause?"
                            onConfirm={(onSuccess) =>
                                deleteLossCause(
                                    { id: row.id },
                                    {
                                        onSuccess: () => {
                                            onSuccess();
                                        },
                                        onError: (error) => {
                                            errorMessage.show(error);
                                        },
                                    },
                                )
                            }
                        />
                    </Space>
                ),
                fixed: 'right',
            }),
        ],
        [columnHelper, deleteLossCause],
    );

    const tableProps = usePrismaTable({
        data: lossCauses,
        total: data?.total ?? 0,
        loading: isLoading,
        columns,
        rowKey: 'id',
        pagination: { position: 'none' },
    });

    // Drawer handlers
    const openCreateDrawer = () => {
        setEditingId(null);
        form.resetFields();
        form.setFieldsValue({
            primaryLossCause: '',
            secondaryLossCause: '',
            lossCauseKey: '',
            plainDescription: null,
            classificationRule: null,
            aiKeywords: null,
            exampleClaimDescription: null,
            sortOrder: 0,
            isActive: true,
        });
        setDrawerOpen(true);
    };

    const openEditDrawer = (lossCause: (typeof lossCauses)[number]) => {
        setEditingId(lossCause.id);
        form.setFieldsValue({
            primaryLossCause: lossCause.primaryLossCause,
            secondaryLossCause: lossCause.secondaryLossCause,
            lossCauseKey: lossCause.lossCauseKey,
            plainDescription: lossCause.plainDescription,
            classificationRule: lossCause.classificationRule,
            aiKeywords: lossCause.aiKeywords,
            exampleClaimDescription: lossCause.exampleClaimDescription,
            sortOrder: lossCause.sortOrder,
            isActive: lossCause.isActive,
        });
        setDrawerOpen(true);
    };

    const closeDrawer = () => {
        setDrawerOpen(false);
        setEditingId(null);
        form.resetFields();
    };

    const onFinish = (values: LossCauseFormInput) => {
        const data = {
            primaryLossCause: values.primaryLossCause,
            secondaryLossCause: values.secondaryLossCause,
            lossCauseKey: values.lossCauseKey,
            plainDescription: values.plainDescription,
            classificationRule: values.classificationRule,
            aiKeywords: values.aiKeywords,
            exampleClaimDescription: values.exampleClaimDescription,
            sortOrder: values.sortOrder,
            isActive: values.isActive,
        };

        if (editingId) {
            updateLossCause({ id: editingId, data });
        } else {
            createLossCause({ productTypeId: BigInt(productTypeId!), data });
        }
    };

    return (
        <PageController route={Route}>
            <div className="mb-6 flex items-start gap-4 rounded-lg border border-info-border bg-info-subtle p-4">
                <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-info-subtle">
                    <GitBranch className="h-5 w-5 text-info" />
                </div>
                <div>
                    <h3 className="mb-1 font-semibold text-info-foreground text-sm">Loss cause taxonomy</h3>
                    <p className="text-info-foreground text-sm">
                        Powers AI-driven claim classification with a flat structure. Each entry defines a primary and
                        secondary loss cause with classification rules and AI keywords that drive automated decisions.
                    </p>
                </div>
            </div>
            <TableToolbar
                actions={
                    <Button type="primary" icon={<Plus />} onClick={openCreateDrawer}>
                        Add loss cause
                    </Button>
                }
            />
            <PrismaTable {...tableProps} />

            <Drawer
                title={editingId ? 'Edit loss cause' : 'Add loss cause'}
                open={drawerOpen}
                onClose={closeDrawer}
                width={500}
                footer={
                    <div className="flex justify-end gap-2">
                        <Button onClick={closeDrawer}>Cancel</Button>
                        <Button type="primary" htmlType="submit" form={formId} loading={isCreating || isUpdating}>
                            {editingId ? 'Save changes' : 'Create'}
                        </Button>
                    </div>
                }
            >
                <Form<LossCauseFormInput> form={form} layout="vertical" onFinish={onFinish} id={formId}>
                    <FormItem
                        label="Primary loss cause"
                        name="primaryLossCause"
                        rules={[{ required: true, message: 'Primary loss cause is required' }]}
                    >
                        <Input placeholder="e.g., Professional liability" />
                    </FormItem>

                    <FormItem
                        label="Secondary loss cause"
                        name="secondaryLossCause"
                        rules={[{ required: true, message: 'Secondary loss cause is required' }]}
                    >
                        <Input placeholder="e.g., Clinical care incident" />
                    </FormItem>

                    <FormItem
                        label="Loss cause key"
                        name="lossCauseKey"
                        rules={[{ required: true, message: 'Key is required' }]}
                        tooltip="Unique identifier for this loss cause"
                    >
                        <Input placeholder="e.g., CLINICAL_CARE_INCIDENT" />
                    </FormItem>

                    <FormItem label="Description" name="plainDescription">
                        <Input.TextArea rows={3} placeholder="Human-readable description for this loss cause" />
                    </FormItem>

                    <FormItem
                        label="Classification rule"
                        name="classificationRule"
                        tooltip="Rule used by the AI for classifying claims into this loss cause"
                    >
                        <Input.TextArea rows={3} placeholder="Describe when a claim should be classified here" />
                    </FormItem>

                    <FormItem
                        label="AI keywords"
                        name="aiKeywords"
                        tooltip="Keywords that help the AI identify this loss cause"
                    >
                        <Input.TextArea rows={3} placeholder="Comma-separated keywords for AI matching" />
                    </FormItem>

                    <FormItem
                        label="Example claim description"
                        name="exampleClaimDescription"
                        tooltip="An example claim narrative that would match this loss cause"
                    >
                        <Input.TextArea rows={3} placeholder="e.g., Patient sustained injury during treatment..." />
                    </FormItem>

                    <div className="mt-4 border-separator border-t pt-4">
                        <FormItem label="Sort order" name="sortOrder" tooltip="Lower numbers appear first">
                            <InputNumber min={0} className="w-full" />
                        </FormItem>

                        <FormItem label="Active" name="isActive" valuePropName="checked">
                            <Switch />
                        </FormItem>
                    </div>
                </Form>
            </Drawer>
        </PageController>
    );
}
